import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleAuthButton from '../auth/GoogleAuthButton';
import SessionList from './SessionList';
import StartPartyDialog from './StartPartyDialog';
import Modal from '../ui/Modal';
import useUserStore from '../../stores/userStore';
import { joinParty } from '../../services/eventLifecycle';
import { initializeAuthSession, loadSessionHistory } from '../../services/authSession';
import { signOutCurrentUser } from '../../services/authProviders';
import { clearYouTubeAuthorization } from '../../services/youtubeAuth';
import { clearSessionEventId } from '../../services/sessionRef';
import { isValidEventCodeShape, normalizeEventCode } from '../../utils/eventCode';
import { partitionSessions, isIntentionalLeave } from '../../utils/sessionHistory';
import { isEventEnded } from '../../utils/eventStatus';
import { describeError } from '../../utils/errorLog';

/**
 * Rhythmé home.
 *
 * Anonymous visitors can still join a party by code with no sign-in. Signing in
 * with Google adds hosting and session history. Nothing here decides a party
 * role: that stays with event.hostId.
 */
export default function HomeScreen() {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const sessions = useUserStore((state) => state.sessions);
  const sessionsLoading = useUserStore((state) => state.sessionsLoading);
  const isGoogleAccount = user?.isGoogleAccount === true;

  const [code, setCode] = React.useState('');
  const [guestName, setGuestName] = React.useState('');
  const [joining, setJoining] = React.useState(false);
  const [openingEventId, setOpeningEventId] = React.useState(null);
  const [startOpen, setStartOpen] = React.useState(false);
  const [djSoonOpen, setDjSoonOpen] = React.useState(false);

  React.useEffect(() => {
    if (!user?.uid) return;
    loadSessionHistory(user.uid).catch(() => {});
  }, [user?.uid]);

  const groups = React.useMemo(() => partitionSessions(sessions), [sessions]);

  const enterParty = (createdEvent) => {
    if (!createdEvent?.id) return;
    setStartOpen(false);
    navigate(`/party/${createdEvent.id}`, {
      state: { inviteAfterCreate: true, eventId: createdEvent.id },
    });
  };

  const handleJoin = async (submitEvent) => {
    submitEvent.preventDefault();
    if (joining) return;

    const normalized = normalizeEventCode(code);
    if (!isValidEventCodeShape(normalized)) {
      toast.error('Enter a valid event code');
      return;
    }

    if (!isGoogleAccount && !guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setJoining(true);
    try {
      const result = await joinParty({ eventCode: normalized, displayName: guestName });
      if (result.pending) {
        toast('Waiting for the host to approve');
        navigate(`/join/${result.eventData.id}`);
        return;
      }
      toast.success(`Joined ${result.eventData.title}`);
      navigate(`/party/${result.eventData.id}`);
    } catch (error) {
      if (error?.code === 'event-ended') {
        toast.error('This party has ended');
        return;
      }
      if (error?.code === 'join-denied') {
        toast.error('The host declined your request');
        return;
      }
      toast.error(error?.code === 'not-found' ? 'Event not found' : 'Could not join that party');
    } finally {
      setJoining(false);
    }
  };

  const handleOpenSession = async (session) => {
    if (isIntentionalLeave(session) && !isEventEnded(session)) {
      navigate(`/join/${session.eventId}`);
      return;
    }

    setOpeningEventId(session.eventId);
    try {
      const result = await joinParty({ eventCode: session.eventId });
      if (result.pending) {
        toast('Waiting for the host to approve');
        navigate(`/join/${result.eventData.id}`);
        return;
      }
      navigate(`/party/${result.eventData.id}`);
    } catch (error) {
      if (error?.code === 'event-ended') {
        toast.error('This party has ended');
        return;
      }
      toast.error(error?.code === 'not-found' ? 'That party no longer exists' : 'Could not open that party');
    } finally {
      setOpeningEventId(null);
    }
  };

  const handleSignOut = async () => {
    try {
      clearYouTubeAuthorization();
      clearSessionEventId();
      await signOutCurrentUser();
      await initializeAuthSession();
      toast.success('Signed out');
    } catch (error) {
      console.error('Sign out failed:', describeError(error));
      toast.error('Could not sign out');
    }
  };

  return (
    <div className="rhythm-page bg-gradient-to-b from-gray-900 to-black">
      <div className="rhythm-page-shell rhythm-page-shell--home" data-testid="home-page-shell">
        <header className="text-center">
          <h1 className="rhythm-page-title font-bold text-white tracking-tight">Rhythmé</h1>
          <p className="rhythm-page-lede text-gray-400">One room, one speaker, everyone&apos;s music.</p>
        </header>

        {isGoogleAccount ? (
          <div className="rhythm-home-account rounded-xl bg-white/5">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" className="rhythm-home-account__avatar rounded-full object-cover" />
            ) : (
              <div className="rhythm-home-account__avatar flex items-center justify-center rounded-full bg-blue-600 text-white">
                {(user.displayName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">
                {user.displayName || 'Signed in'}
              </p>
              <p className="text-xs text-gray-400">Google account</p>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20 transition-colors"
            >
              Sign out
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <GoogleAuthButton />
            <p className="text-center text-xs text-gray-500">
              Optional. You can join a party without an account.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {isGoogleAccount && (
            <section className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Personal</h2>
              <button
                type="button"
                onClick={() => navigate('/listen')}
                className="rhythm-home-cta w-full rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 transition-colors"
              >
                Listen for Yourself
              </button>
              <p className="text-xs text-gray-500">Your own Rhythmé music space.</p>
            </section>
          )}

          <section className="space-y-2">
            {isGoogleAccount && (
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Party</h2>
            )}
            <button
              type="button"
              onClick={() => setStartOpen(true)}
              className="rhythm-home-cta w-full rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 transition-colors"
            >
              Start a Party
            </button>
            {isGoogleAccount && (
              <button
                type="button"
                onClick={() => setDjSoonOpen(true)}
                className="rhythm-home-cta w-full rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 transition-colors"
              >
                Are you a DJ?
              </button>
            )}
          </section>

          <form onSubmit={handleJoin} className="space-y-2">
            <label htmlFor="joinCode" className="block text-sm font-medium text-gray-300">
              Join a Party
            </label>

            {!isGoogleAccount && (
              <input
                id="guestName"
                type="text"
                value={guestName}
                onChange={(changeEvent) => setGuestName(changeEvent.target.value)}
                placeholder="Your name"
                aria-label="Your name"
                className="rhythm-home-input w-full rounded-lg bg-white/10 border border-white/20 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            )}

            <div className="flex gap-2">
              <input
                id="joinCode"
                type="text"
                value={code}
                onChange={(changeEvent) => setCode(changeEvent.target.value.toUpperCase())}
                placeholder="Event code"
                maxLength={12}
                className="rhythm-home-input min-w-0 flex-1 rounded-lg bg-white/10 border border-white/20 px-4 font-mono tracking-widest text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="submit"
                disabled={joining}
                className="rhythm-home-input shrink-0 rounded-lg bg-purple-600 px-5 font-medium text-white hover:bg-purple-700 transition-colors disabled:opacity-60"
              >
                {joining ? 'Joining…' : 'Join'}
              </button>
            </div>
          </form>
        </div>

        {isGoogleAccount && (
          <div className="space-y-6">
            {sessionsLoading && <p className="text-sm text-gray-500">Loading your sessions…</p>}
            <SessionList
              title="Active Sessions"
              sessions={groups.active}
              emptyMessage="No active parties right now."
              onOpen={handleOpenSession}
              busyEventId={openingEventId}
            />
            <SessionList
              title="Previous Sessions"
              sessions={groups.previous}
              emptyMessage="Parties you host or join will show up here."
              onOpen={handleOpenSession}
              busyEventId={openingEventId}
              collapsible
              defaultCollapsed
            />
          </div>
        )}
      </div>

      <StartPartyDialog
        open={startOpen}
        onClose={() => setStartOpen(false)}
        onEnterParty={enterParty}
      />

      <Modal
        open={djSoonOpen}
        title="Are you a DJ?"
        onClose={() => setDjSoonOpen(false)}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-300">
            Run an event using streaming and your own music.
          </p>
          <p className="text-sm text-gray-500">DJ Mode is coming soon.</p>
          <button
            type="button"
            onClick={() => setDjSoonOpen(false)}
            className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </Modal>
    </div>
  );
}
